import { useState, useEffect, useMemo } from "react";
import { listQuestions, createQuestion, updateQuestion, deleteQuestion, reorderQuestions, listTriggers, getScoringAudit } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, X, Check, GripVertical, ArrowUp, ArrowDown, AlertTriangle, ChevronDown, ChevronRight, Search, Settings2, BarChart3, Bot } from "lucide-react";

// --- Assessment Type metadata (Level 1) ---
const ASSESSMENTS = [
  { key: "greenfield", label: "Greenfield", dot: "#16A34A", bg: "#DCFCE7", text: "#166534", border: "#16A34A" },
  { key: "brownfield", label: "Brownfield", dot: "#EA580C", bg: "#FFEDD5", text: "#9A3412", border: "#EA580C" },
  { key: "cloud_migration", label: "Cloud Migration", dot: "#2563EB", bg: "#DBEAFE", text: "#1E40AF", border: "#2563EB" },
];
const ASSESSMENT_MAP = Object.fromEntries(ASSESSMENTS.map(a => [a.key, a]));

// --- Module metadata (Level 2) ---
const MODULE_NUM = { module1: "Module 1", module2: "Module 2", module3: "Module 3" };
const MODULE_NAME = { module1: "Company Profile", module2: "Warehouse Landscape", module3: "Process Evaluation" };
const MODULE_ORDER = ["module1", "module2", "module3"];

// --- Section labels (Level 3) — friendly names keyed by section key ---
const SECTION_LABELS = {
  client_details: "Client Details & Assessment Context",
  warehouse_context: "Business Overview",
  migration_landscape: "SAP Landscape & Scope",
  warehouse_network: "Warehouse Network",
  representative_sites: "Representative Warehouse Selection",
  warehouse_scale: "Warehouse Scale & Complexity",
  org: "Warehouse Profile & Volumes",
  inbound: "Inbound Processes",
  outbound: "Outbound Processes",
  inventory: "Internal Movements & Inventory",
  production: "Production & Quality",
  automation: "Smart Warehousing",
  integration: "Integrations & Interfaces",
  reporting: "Reporting & KPIs",
  pain_points: "Business Priorities & Future Strategy",
  migration_readiness: "Current System & Migration Readiness",
  system_infrastructure: "System & Infrastructure",
  data_volume: "Data & Volumes",
  integration_landscape: "Integration Landscape",
  custom_development: "Custom Development",
  operations_cutover: "Operations & Cutover",
  migration_objectives: "Migration Objectives",
  current_parallel_projects: "Current & Parallel Projects",
};
const humanize = (k) => String(k || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const sectionNavLabel = (key) => SECTION_LABELS[key] || humanize(key);

// --- Form section/sub-section mapping (extended with Cloud Migration sections so
// editing a CM question preserves its section instead of re-bucketing it). ---
const SECTION_OPTIONS = [
  { value: "module1", label: "Module 1 · Company Profile" },
  { value: "module2", label: "Module 2 · Warehouse Landscape" },
  { value: "module3", label: "Module 3 · Process Evaluation" },
];

const SUBSECTIONS = {
  module1: [
    { value: "client_details", label: "General Client Information" },
    { value: "client_details::Assessment Context", label: "Assessment Context", subsection_heading: "Assessment Context" },
  ],
  module2: [
    { value: "warehouse_context", label: "Business Overview" },
    { value: "warehouse_context::Current SAP Landscape", label: "Current SAP Landscape", subsection_heading: "Current SAP Landscape" },
    { value: "warehouse_network", label: "Warehouse Network" },
    { value: "representative_sites", label: "Representative Warehouse Selection" },
    { value: "warehouse_scale", label: "Warehouse Scale & Complexity" },
    { value: "migration_landscape", label: "SAP Landscape & Scope (Cloud Migration)" },
  ],
  module3: [
    { value: "org", label: "Warehouse Profile & Volumes" },
    { value: "inbound", label: "Inbound Processes" },
    { value: "outbound", label: "Outbound Processes" },
    { value: "inventory", label: "Internal Movements & Inventory" },
    { value: "production", label: "Production & Quality" },
    { value: "automation", label: "Smart Warehousing" },
    { value: "integration", label: "Integrations & Interfaces" },
    { value: "reporting", label: "Reporting & KPIs" },
    { value: "pain_points", label: "Business Priorities & Future Strategy" },
    { value: "migration_readiness", label: "Current System & Migration Readiness (Brownfield)" },
    { value: "system_infrastructure", label: "System & Infrastructure (Cloud Migration)" },
    { value: "data_volume", label: "Data & Volumes (Cloud Migration)" },
    { value: "integration_landscape", label: "Integration Landscape (Cloud Migration)" },
    { value: "custom_development", label: "Custom Development (Cloud Migration)" },
    { value: "operations_cutover", label: "Operations & Cutover (Cloud Migration)" },
    { value: "migration_objectives", label: "Migration Objectives (Cloud Migration)" },
    { value: "current_parallel_projects", label: "Current & Parallel Projects (Cloud Migration)" },
  ],
};

const IMPACT_AREAS = [
  { value: "org", label: "Org Structure" },
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" },
  { value: "inventory", label: "Inventory" },
  { value: "production", label: "Production" },
  { value: "automation", label: "Automation" },
  { value: "integration", label: "Integration" },
  { value: "reporting", label: "Reporting" },
  { value: "pain_points", label: "Pain Points" },
  { value: "informational_only", label: "Informational Only" },
];

const CAPABILITY_TRIGGERS_FALLBACK = [];
const NONE_OPTION = { value: "none", label: "None" };

// Reverse lookups
const impactLabel = (v) => IMPACT_AREAS.find(i => i.value === v)?.label || v;
const triggerLabelFrom = (list) => (v) => list.find(i => i.value === v)?.label || v;

// Sub-section options can carry a composite `value` ("<section>::<heading>") to
// distinguish two sub-sections that share the same `section`.
const subSectionOf = (s) => s.section || (s.value || "").split("::")[0];
const subHeadingOf = (s) => s.subsection_heading || "";
const currentSubValue = (form) => {
  const subs = SUBSECTIONS[form.module] || [];
  const exact = subs.find(s => subSectionOf(s) === form.section && subHeadingOf(s) === (form.subsection_heading || ""));
  if (exact) return exact.value;
  const bySection = subs.find(s => subSectionOf(s) === form.section);
  return bySection ? bySection.value : (subs[0]?.value || form.section);
};

const ANSWER_TYPE_LABELS = {
  single_select: "Single Select",
  multi_select: "Multi Select",
  dropdown: "Dropdown",
  number: "Number",
  text: "Text",
};
const NON_SCORING_TYPES = new Set(["multi_select", "dropdown", "number", "text"]);
const NUMERIC_TYPES = new Set(["number"]);
const TEXT_TYPES = new Set(["text"]);

// Auto-generate a stable field_key from the question text
const slugifyKey = (text) =>
  String(text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60) || `q_${Date.now().toString(36)}`;

const DEFAULT_SCORING_OPTS = [
  { value: "low", label: "Low complexity" },
  { value: "medium", label: "Medium complexity" },
  { value: "high", label: "High complexity" },
];

const EVAL_TYPE_OPTIONS = [
  { value: "greenfield", label: "Greenfield" },
  { value: "brownfield", label: "Brownfield" },
  { value: "cloud_migration", label: "Cloud Migration" },
];

const EVAL_METHOD_OPTIONS = [
  { value: "manual", label: "Manual Assessment" },
  { value: "quantitative", label: "Quantitative Assessment" },
  { value: "system_derived", label: "System Derived (Future)" },
];
const EVAL_METHOD_META = {
  manual: { label: "Manual", Icon: Settings2, bg: "#F1F5F9", text: "#475569" },
  quantitative: { label: "Quantitative", Icon: BarChart3, bg: "#ECFEFF", text: "#0E7490" },
  system_derived: { label: "System Derived", Icon: Bot, bg: "#F5F3FF", text: "#6D28D9" },
};

const EMPTY = {
  module: "module3", section: "org", question_text: "", answer_type: "single_select",
  answer_options: [...DEFAULT_SCORING_OPTS], max_selections: null,
  active: true, display_order: 0, help_text: "", conditional_visibility: "",
  score_category: "org", feature_flag_mapping: "none", scoring_mode: "standard",
  numeric_mapping_low: 1, numeric_mapping_medium: 2, numeric_mapping_high: 3,
  field_key: "", placeholder: "", min_value: null, max_value: null,
  is_required: true, default_value: "", subsection_heading: "",
  evaluation_types: ["greenfield", "brownfield"],
  evaluation_method: "manual",
};

// Assessment badge pill
function AssessmentBadge({ type }) {
  const a = ASSESSMENT_MAP[type];
  if (!a) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-semibold whitespace-nowrap"
      style={{ backgroundColor: a.bg, color: a.text }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: a.dot }} />
      {a.label}
    </span>
  );
}

// Evaluation method badge pill
function EvalMethodBadge({ method }) {
  const m = EVAL_METHOD_META[method] || EVAL_METHOD_META.manual;
  const Icon = m.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-semibold whitespace-nowrap"
      style={{ backgroundColor: m.bg, color: m.text }}
    >
      <Icon className="w-2.5 h-2.5" />
      {m.label}
    </span>
  );
}

export default function QuestionsPage() {
  const [items, setItems] = useState([]);
  const [triggers, setTriggers] = useState(CAPABILITY_TRIGGERS_FALLBACK);
  const [activeAssessment, setActiveAssessment] = useState("greenfield");
  const [activeModule, setActiveModule] = useState("module1");
  const [activeSection, setActiveSection] = useState(null);
  // Secondary filters
  const [fMethod, setFMethod] = useState("all");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [search, setSearch] = useState("");
  // Form + CRUD state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [msg, setMsg] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const load = () => listQuestions().then(setItems).catch(() => {});
  const loadTriggers = () => listTriggers()
    .then(list => {
      const active = (list || [])
        .filter(t => t.is_active && t.trigger_id !== "none")
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(t => ({ value: t.trigger_id, label: t.trigger_name }));
      setTriggers([NONE_OPTION, ...active]);
    })
    .catch(() => setTriggers([NONE_OPTION]));
  const loadAudit = () => getScoringAudit().then(setAudit).catch(() => setAudit(null));
  useEffect(() => { load(); loadTriggers(); loadAudit(); }, []);

  const triggerLabel = useMemo(() => triggerLabelFrom(triggers), [triggers]);

  const hasType = (q) => (q.evaluation_types || []).includes(activeAssessment);

  // Build the Assessment → Module → Section tree from live questions.
  const tree = useMemo(() => {
    const forType = items.filter(hasType);
    const mods = {}; // module -> { section -> { count, minOrder } }
    for (const q of forType) {
      const mod = q.module || "module3";
      const sec = q.section || "unknown";
      mods[mod] = mods[mod] || {};
      const cur = mods[mod][sec] || { count: 0, minOrder: Infinity };
      cur.count += 1;
      cur.minOrder = Math.min(cur.minOrder, q.display_order || 0);
      mods[mod][sec] = cur;
    }
    const modules = MODULE_ORDER
      .filter(m => mods[m])
      .map(m => ({
        key: m,
        sections: Object.entries(mods[m])
          .map(([sec, meta]) => ({ key: sec, label: sectionNavLabel(sec), count: meta.count, minOrder: meta.minOrder }))
          .sort((a, b) => (a.minOrder - b.minOrder) || a.label.localeCompare(b.label)),
      }));
    return { modules };
  }, [items, activeAssessment]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalsByAssessment = useMemo(() => {
    const t = { greenfield: 0, brownfield: 0, cloud_migration: 0 };
    for (const q of items) for (const et of (q.evaluation_types || [])) if (t[et] != null) t[et] += 1;
    return t;
  }, [items]);

  // Keep module/section selection valid whenever the tree changes.
  useEffect(() => {
    const modules = tree.modules;
    if (!modules.length) { setActiveModule(null); setActiveSection(null); return; }
    let mod = modules.find(m => m.key === activeModule) || modules[0];
    if (mod.key !== activeModule) setActiveModule(mod.key);
    const sections = mod.sections;
    if (!sections.length) { setActiveSection(null); return; }
    if (!sections.find(s => s.key === activeSection)) setActiveSection(sections[0].key);
  }, [tree]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentModule = tree.modules.find(m => m.key === activeModule) || null;
  const currentSections = currentModule?.sections || [];

  const gridItems = useMemo(() => {
    let list = items.filter(q => hasType(q) && q.module === activeModule && q.section === activeSection);
    if (fMethod !== "all") list = list.filter(q => (q.evaluation_method || "manual") === fMethod);
    if (fType !== "all") list = list.filter(q => q.answer_type === fType);
    if (fStatus !== "all") list = list.filter(q => (fStatus === "active" ? q.active : !q.active));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(q => (q.question_text || "").toLowerCase().includes(s) || (q.field_key || "").toLowerCase().includes(s));
    }
    return list.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }, [items, activeAssessment, activeModule, activeSection, fMethod, fType, fStatus, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtersActive = fMethod !== "all" || fType !== "all" || fStatus !== "all" || search.trim() !== "";
  const canReorder = !filtersActive; // drag/order only meaningful on the full section list

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(""), 2500); };

  const persistOrder = async (orderedIds, optimisticItems) => {
    if (optimisticItems) setItems(optimisticItems);
    setReordering(true);
    try {
      await reorderQuestions(orderedIds);
      await load();
      showMsg("Order updated");
    } catch {
      showMsg("Failed to save new order");
      await load();
    } finally {
      setReordering(false);
    }
  };

  const buildReordered = (newTabOrder) => {
    const tabIds = new Set(newTabOrder.map(q => q.id));
    const withNewOrders = newTabOrder.map((q, idx) => ({ ...q, display_order: idx + 1 }));
    const byId = Object.fromEntries(withNewOrders.map(q => [q.id, q]));
    return items.map(q => (tabIds.has(q.id) ? byId[q.id] : q));
  };

  const moveUp = (idx) => {
    if (idx <= 0 || reordering || !canReorder) return;
    const next = [...gridItems];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    persistOrder(next.map(q => q.id), buildReordered(next));
  };

  const moveDown = (idx) => {
    if (idx >= gridItems.length - 1 || reordering || !canReorder) return;
    const next = [...gridItems];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    persistOrder(next.map(q => q.id), buildReordered(next));
  };

  const onDragStart = (e, id) => {
    if (!canReorder) return;
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };
  const onDragOver = (e, id) => {
    if (!canReorder) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragOverId) setDragOverId(id);
  };
  const onDragLeave = (id) => { if (dragOverId === id) setDragOverId(null); };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId || !canReorder) return;
    const fromIdx = gridItems.findIndex(q => q.id === sourceId);
    const toIdx = gridItems.findIndex(q => q.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...gridItems];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    persistOrder(next.map(q => q.id), buildReordered(next));
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  const availableSubsections = SUBSECTIONS[form.module] || [];

  const openAdd = () => {
    const mod = activeModule || "module3";
    const sec = activeSection || "org";
    const isInfo = mod !== "module3" || activeAssessment === "cloud_migration";
    setEditing(null);
    setForm({
      ...EMPTY,
      module: mod, section: sec,
      score_category: isInfo ? "informational_only" : sec,
      answer_options: isInfo ? [] : [...DEFAULT_SCORING_OPTS],
      answer_type: isInfo ? "text" : "single_select",
      evaluation_types: [activeAssessment],
    });
    setShowForm(true);
  };

  const openEdit = (q) => {
    setEditing(q.id);
    setForm({ ...EMPTY, ...q, feature_flag_mapping: q.feature_flag_mapping || "none" });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.question_text.trim()) { setMsg("Question text is required"); setTimeout(() => setMsg(""), 3000); return; }
    if (form.active && (!Array.isArray(form.evaluation_types) || form.evaluation_types.length === 0)) {
      setMsg("Select at least one applicable evaluation type"); setTimeout(() => setMsg(""), 3000); return;
    }
    const payload = { ...form };
    if (payload.feature_flag_mapping === "none" || !payload.feature_flag_mapping) {
      payload.feature_flag_mapping = null;
    }
    if (!payload.field_key || !String(payload.field_key).trim()) {
      payload.field_key = slugifyKey(payload.question_text);
    }
    if (NON_SCORING_TYPES.has(payload.answer_type)) {
      payload.score_category = "informational_only";
      payload.feature_flag_mapping = null;
    }
    if (NUMERIC_TYPES.has(payload.answer_type) || TEXT_TYPES.has(payload.answer_type)) {
      payload.answer_options = [];
    }
    payload.is_required = payload.is_required === false ? false : true;
    if (payload.answer_type === "single_select" && payload.score_category !== "informational_only") {
      const lo = (payload.answer_options || []).find(o => o.value === "low")?.label || "Low";
      const md = (payload.answer_options || []).find(o => o.value === "medium")?.label || "Medium";
      const hi = (payload.answer_options || []).find(o => o.value === "high")?.label || "High";
      payload.answer_options = [{ value: "low", label: lo }, { value: "medium", label: md }, { value: "high", label: hi }];
    }
    try {
      if (editing) await updateQuestion(editing, payload);
      else await createQuestion(payload);
      setEditing(null); setShowForm(false); setForm({ ...EMPTY });
      setMsg("Question saved successfully"); load(); loadAudit();
      setTimeout(() => setMsg(""), 2500);
    } catch { setMsg("Error saving question"); setTimeout(() => setMsg(""), 3000); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteQuestion(deleteTarget);
      setDeleteTarget(null);
      setMsg("Question deleted"); load(); loadAudit();
      setTimeout(() => setMsg(""), 2500);
    } catch { setMsg("Error deleting"); setTimeout(() => setMsg(""), 3000); }
  };

  const cancel = () => { setShowForm(false); setEditing(null); setForm({ ...EMPTY }); };

  const activeSectionLabel = currentSections.find(s => s.key === activeSection)?.label || "Questions";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>Question Management</h1>
        <Button data-testid="add-question-btn" onClick={openAdd} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Question
        </Button>
      </div>
      <p className="text-xs text-[#9CA3AF] mb-4">
        Browse the question bank by Assessment → Module → Section. Edits here stream directly to the live assessment. Cloud Migration questions are informational only and never affect scoring.
      </p>

      {audit && audit.total_issues > 0 && (
        <div data-testid="scoring-audit-banner" className="mb-4 border border-[#F59E0B] bg-[#FEF3C7] rounded-sm">
          <button type="button" onClick={() => setAuditOpen(v => !v)} className="w-full flex items-start gap-2 px-3 py-2.5 text-left" data-testid="scoring-audit-toggle">
            <AlertTriangle className="w-4 h-4 text-[#B45309] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-[#92400E]">
                {audit.total_issues} question{audit.total_issues === 1 ? "" : "s"} will be silently ignored by the scoring engine
              </div>
              <div className="text-[11px] text-[#92400E]/80 mt-0.5">
                These questions exist in the admin panel but are not wired into the hard-coded scoring logic. Click to review.
              </div>
            </div>
            {auditOpen ? <ChevronDown className="w-4 h-4 text-[#B45309] mt-0.5" /> : <ChevronRight className="w-4 h-4 text-[#B45309] mt-0.5" />}
          </button>
          {auditOpen && (
            <div className="px-3 pb-3 space-y-3 text-[11px] text-[#78350F]" data-testid="scoring-audit-details">
              {audit.unmapped_section_questions?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Not included in section scoring ({audit.unmapped_section_questions.length})</div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {audit.unmapped_section_questions.map(q => (
                      <li key={q.id}><span className="font-mono">{q.field_key}</span> — {q.section} — {q.question_text}</li>
                    ))}
                  </ul>
                </div>
              )}
              {audit.unknown_trigger_questions?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Trigger not wired to any capability ({audit.unknown_trigger_questions.length})</div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {audit.unknown_trigger_questions.map(q => (
                      <li key={q.id}><span className="font-mono">{q.field_key}</span> → <span className="font-mono">{q.trigger_id}</span> — {q.question_text}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {msg && <div className="mb-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979] font-medium">{msg}</div>}

      {/* LEVEL 1 — Assessment Type tabs */}
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="assessment-tabs">
        {ASSESSMENTS.map(a => {
          const active = activeAssessment === a.key;
          return (
            <button
              key={a.key}
              data-testid={`assessment-tab-${a.key}`}
              onClick={() => { setActiveAssessment(a.key); setShowForm(false); }}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-sm border transition-colors ${active ? "text-white" : "bg-white text-[#374151] hover:bg-gray-50"}`}
              style={active ? { backgroundColor: a.border, borderColor: a.border } : { borderColor: "#E5E7EB" }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? "#FFFFFF" : a.dot }} />
              {a.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${active ? "bg-white/25" : "bg-gray-100 text-[#6B7280]"}`}>
                {totalsByAssessment[a.key] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* LEVEL 2 — Module tabs */}
      <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="module-tabs">
        {tree.modules.map(m => {
          const active = activeModule === m.key;
          const total = m.sections.reduce((s, x) => s + x.count, 0);
          return (
            <button
              key={m.key}
              data-testid={`module-tab-${m.key}`}
              onClick={() => { setActiveModule(m.key); setActiveSection(m.sections[0]?.key || null); setShowForm(false); }}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${active ? "bg-[#025979] text-white border-[#025979]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#D1D5DB] hover:text-[#111827]"}`}
            >
              <span className="font-semibold">{MODULE_NUM[m.key]}</span>
              <span className={active ? "text-white/80" : "text-[#9CA3AF]"}> · {MODULE_NAME[m.key]}</span>
              <span className={`ml-1.5 ${active ? "text-white/70" : "text-[#9CA3AF]"}`}>({total})</span>
            </button>
          );
        })}
        {tree.modules.length === 0 && (
          <span className="text-xs text-[#9CA3AF] px-2 py-1.5">No modules with questions for this assessment.</span>
        )}
      </div>

      <div className="flex gap-4">
        {/* LEVEL 3 — Section list */}
        <div className="w-56 shrink-0" data-testid="section-list">
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-[#E5E7EB] text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">Sections</div>
            <div className="max-h-[520px] overflow-y-auto">
              {currentSections.map(s => {
                const active = activeSection === s.key;
                return (
                  <button
                    key={s.key}
                    data-testid={`section-pill-${s.key}`}
                    onClick={() => { setActiveSection(s.key); setShowForm(false); }}
                    className={`w-full text-left px-3 py-2 text-xs border-b border-[#F3F4F6] transition-colors flex items-center justify-between gap-2 ${active ? "bg-[#E0F2FE] text-[#025979] font-semibold border-l-2 border-l-[#025979]" : "text-[#374151] hover:bg-gray-50"}`}
                  >
                    <span className="leading-tight">{s.label}</span>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9px] ${active ? "bg-[#025979] text-white" : "bg-gray-100 text-[#6B7280]"}`}>{s.count}</span>
                  </button>
                );
              })}
              {currentSections.length === 0 && (
                <div className="px-3 py-6 text-center text-[11px] text-[#9CA3AF]">No sections.</div>
              )}
            </div>
          </div>
        </div>

        {/* Right column: filters + grid */}
        <div className="flex-1 min-w-0">
          {/* Filters */}
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="question-filters">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="filter-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search question or field key…" className="rounded-sm text-xs h-8 pl-8" />
            </div>
            <Select value={fMethod} onValueChange={setFMethod}>
              <SelectTrigger data-testid="filter-method" className="rounded-sm text-xs h-8 w-40"><SelectValue placeholder="Eval Method" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                {EVAL_METHOD_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fType} onValueChange={setFType}>
              <SelectTrigger data-testid="filter-type" className="rounded-sm text-xs h-8 w-36"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="single_select">Single Select</SelectItem>
                <SelectItem value="multi_select">Multi Select</SelectItem>
                <SelectItem value="dropdown">Dropdown</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="text">Text</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger data-testid="filter-status" className="rounded-sm text-xs h-8 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {filtersActive && (
              <button data-testid="clear-filters" onClick={() => { setSearch(""); setFMethod("all"); setFType("all"); setFStatus("all"); }} className="text-xs text-[#025979] hover:text-[#03738C] font-medium">Clear</button>
            )}
          </div>

          {/* Add/Edit Form Panel */}
          {showForm && (() => {
            const isScoring = form.answer_type === "single_select" && form.score_category !== "informational_only";
            const isNumeric = NUMERIC_TYPES.has(form.answer_type);
            const isText = TEXT_TYPES.has(form.answer_type);
            const hasFreeOptions = form.answer_type === "multi_select" || form.answer_type === "dropdown"
              || (form.answer_type === "single_select" && form.score_category === "informational_only");
            return (
            <div className="bg-white border border-[#025979]/20 rounded-sm shadow-sm mb-5 p-5" data-testid="question-form">
              <h2 className="text-sm font-semibold text-[#111827] mb-4" style={{ fontFamily: "Chivo" }}>
                {editing ? "Edit Question" : "Add New Question"}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-[#374151] mb-1.5 block">Question Text <span className="text-red-500">*</span></label>
                  <Input data-testid="q-text-input" value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })} className="rounded-sm text-sm" placeholder="e.g. What is the automation level in this warehouse?" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Module</label>
                    <Select value={form.module} onValueChange={v => {
                      const subs = SUBSECTIONS[v] || [];
                      setForm({ ...form, module: v, section: subSectionOf(subs[0] || {}) || "", subsection_heading: subHeadingOf(subs[0] || {}) });
                    }}>
                      <SelectTrigger data-testid="q-section-select" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{SECTION_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Section</label>
                    <Select value={currentSubValue(form)} onValueChange={v => {
                      const sub = (SUBSECTIONS[form.module] || []).find(s => s.value === v);
                      if (sub) setForm({ ...form, section: subSectionOf(sub), subsection_heading: subHeadingOf(sub) });
                    }}>
                      <SelectTrigger data-testid="q-subsection-select" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{availableSubsections.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Answer Type</label>
                    <Select value={form.answer_type} onValueChange={v => {
                      const next = { ...form, answer_type: v };
                      if (NON_SCORING_TYPES.has(v)) {
                        next.score_category = "informational_only";
                        next.feature_flag_mapping = "none";
                        if (NUMERIC_TYPES.has(v) || TEXT_TYPES.has(v)) next.answer_options = [];
                        else if (!(next.answer_options || []).length) next.answer_options = [];
                      }
                      if (v === "single_select" && next.score_category === "informational_only") {
                        next.score_category = form.section === "org" ? "org" : "informational_only";
                        next.answer_options = next.answer_options?.length ? next.answer_options : [...DEFAULT_SCORING_OPTS];
                      }
                      setForm(next);
                    }}>
                      <SelectTrigger data-testid="q-answer-type-select" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single_select">Single Select (Scoring)</SelectItem>
                        <SelectItem value="multi_select">Multi Select</SelectItem>
                        <SelectItem value="dropdown">Dropdown</SelectItem>
                        <SelectItem value="number">Number Input</SelectItem>
                        <SelectItem value="text">Text Input</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {!NON_SCORING_TYPES.has(form.answer_type) && (
                    <div>
                      <label className="text-xs font-medium text-[#374151] mb-1.5 block">Impact Area <span className="text-red-500">*</span></label>
                      <Select value={form.score_category} onValueChange={v => setForm({ ...form, score_category: v })}>
                        <SelectTrigger data-testid="q-impact-select" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{IMPACT_AREAS.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {!NON_SCORING_TYPES.has(form.answer_type) && (
                    <div>
                      <label className="text-xs font-medium text-[#374151] mb-1.5 block">Advanced Capability Trigger</label>
                      <Select value={form.feature_flag_mapping} onValueChange={v => setForm({ ...form, feature_flag_mapping: v })}>
                        <SelectTrigger data-testid="q-trigger-select" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{triggers.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Display Order</label>
                    <Input data-testid="q-order-input" type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} className="rounded-sm text-xs" />
                  </div>
                  {form.answer_type === "multi_select" && (
                    <div>
                      <label className="text-xs font-medium text-[#374151] mb-1.5 block">Max Selections <span className="text-[#9CA3AF] font-normal">(optional)</span></label>
                      <Input
                        data-testid="q-max-selections-input"
                        type="number"
                        min={1}
                        value={form.max_selections ?? ""}
                        onChange={e => {
                          const v = e.target.value;
                          setForm({ ...form, max_selections: v === "" ? null : Math.max(1, parseInt(v) || 1) });
                        }}
                        className="rounded-sm text-xs"
                        placeholder="No limit"
                      />
                      <p className="text-[10px] text-[#9CA3AF] mt-1">Cap on selections for this multi-select. Blank = unlimited.</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Evaluation Method</label>
                    <Select value={form.evaluation_method || "manual"} onValueChange={v => setForm({ ...form, evaluation_method: v })}>
                      <SelectTrigger data-testid="q-eval-method-select" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{EVAL_METHOD_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end pb-1 gap-4">
                    <label className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="rounded-sm" />
                      Active
                    </label>
                    <label className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" data-testid="q-required-toggle" checked={form.is_required !== false} onChange={e => setForm({ ...form, is_required: e.target.checked })} className="rounded-sm" />
                      Required
                    </label>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Applicable Evaluations <span className="text-red-500">*</span></label>
                    <div className="flex flex-wrap gap-4" data-testid="q-evaluation-types">
                      {EVAL_TYPE_OPTIONS.map((opt) => {
                        const selected = (form.evaluation_types || []).includes(opt.value);
                        return (
                          <label key={opt.value} className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" data-testid={`q-eval-${opt.value}`} checked={selected}
                              onChange={(e) => {
                                const cur = new Set(form.evaluation_types || []);
                                if (e.target.checked) cur.add(opt.value); else cur.delete(opt.value);
                                setForm({ ...form, evaluation_types: EVAL_TYPE_OPTIONS.map((o) => o.value).filter((v) => cur.has(v)) });
                              }}
                              className="rounded-sm" />
                            {opt.label}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] mt-1">Controls which assessment types show this question. At least one required for active questions.</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Help Text <span className="text-[#9CA3AF] font-normal">(optional)</span></label>
                    <Input value={form.help_text} onChange={e => setForm({ ...form, help_text: e.target.value })} className="rounded-sm text-xs" placeholder="Optional guidance text shown to users" />
                  </div>
                </div>

                {isNumeric && (
                  <div className="mt-2 bg-gray-50 border border-[#E5E7EB] rounded-sm p-3" data-testid="numeric-config">
                    <label className="text-[11px] font-medium text-[#374151] mb-1 block">Placeholder <span className="text-[#9CA3AF] font-normal">(optional)</span></label>
                    <Input data-testid="q-placeholder-input" value={form.placeholder || ""} onChange={e => setForm({ ...form, placeholder: e.target.value })} className="rounded-sm text-xs h-8" placeholder="e.g. 500000" />
                    <p className="text-[10px] text-[#9CA3AF] mt-1">Numeric input. Positive values only. Set Required above.</p>
                  </div>
                )}
                {isText && (
                  <div className="mt-2 bg-gray-50 border border-[#E5E7EB] rounded-sm p-3" data-testid="text-config">
                    <label className="text-[11px] font-medium text-[#374151] mb-1 block">Placeholder <span className="text-[#9CA3AF] font-normal">(optional)</span></label>
                    <Input data-testid="q-placeholder-input" value={form.placeholder || ""} onChange={e => setForm({ ...form, placeholder: e.target.value })} className="rounded-sm text-xs h-8" placeholder="Optional placeholder text shown in the input" />
                    <p className="text-[10px] text-[#9CA3AF] mt-1">Free-text field. Set Required above.</p>
                  </div>
                )}
                {isScoring && (
                  <div className="mt-4">
                    <label className="text-xs font-medium text-[#374151] mb-2 block">Answer Options</label>
                    <p className="text-[10px] text-[#9CA3AF] mb-3">Standard scoring: Option 1 = Score 1, Option 2 = Score 2, Option 3 = Score 3. Edit labels to match the business context.</p>
                    <div className="space-y-2.5">
                      {[{ key: "low", score: 1, idx: 0 }, { key: "medium", score: 2, idx: 1 }, { key: "high", score: 3, idx: 2 }].map(opt => {
                        const current = (form.answer_options || []).find(o => o.value === opt.key);
                        const fallback = DEFAULT_SCORING_OPTS[opt.idx].label;
                        return (
                          <div key={opt.key} className="flex items-center gap-3">
                            <span className="text-[10px] text-[#9CA3AF] w-20 shrink-0">Option {opt.idx + 1} <span className="text-[#025979] font-medium">(Score {opt.score})</span></span>
                            <Input value={current?.label || fallback}
                              onChange={e => {
                                const lo = opt.key === "low" ? e.target.value : ((form.answer_options || []).find(o => o.value === "low")?.label || DEFAULT_SCORING_OPTS[0].label);
                                const md = opt.key === "medium" ? e.target.value : ((form.answer_options || []).find(o => o.value === "medium")?.label || DEFAULT_SCORING_OPTS[1].label);
                                const hi = opt.key === "high" ? e.target.value : ((form.answer_options || []).find(o => o.value === "high")?.label || DEFAULT_SCORING_OPTS[2].label);
                                setForm({ ...form, answer_options: [{ value: "low", label: lo }, { value: "medium", label: md }, { value: "high", label: hi }] });
                              }}
                              className="rounded-sm text-xs h-7 flex-1" placeholder={fallback} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {hasFreeOptions && (
                  <div className="mt-4" data-testid="free-options">
                    <label className="text-xs font-medium text-[#374151] mb-1.5 block">Answer Options</label>
                    <p className="text-[10px] text-[#9CA3AF] mb-2">This question does not impact scoring. Add one or more options for the user to choose from.</p>
                    <div className="space-y-2">
                      {(form.answer_options || []).map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input value={opt.label}
                            onChange={e => {
                              const opts = [...(form.answer_options || [])];
                              const existing = opts[idx] || {};
                              const nextLabel = e.target.value;
                              const value = existing.value && String(existing.value).trim() ? existing.value : (nextLabel || "").toLowerCase().replace(/[^a-z0-9]+/g, "_");
                              opts[idx] = { ...existing, label: nextLabel, value };
                              setForm({ ...form, answer_options: opts });
                            }}
                            className="rounded-sm text-xs h-7 flex-1" placeholder={`Option ${idx + 1}`} />
                          <button type="button" onClick={() => { const opts = (form.answer_options || []).filter((_, i) => i !== idx); setForm({ ...form, answer_options: opts }); }} className="text-red-400 hover:text-red-600 shrink-0"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                      <button type="button" onClick={() => { const opts = [...(form.answer_options || []), { value: "", label: "" }]; setForm({ ...form, answer_options: opts }); }} className="text-xs text-[#025979] hover:text-[#03738C] font-medium">+ Add Option</button>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-5 flex gap-2 border-t border-[#E5E7EB] pt-4">
                <Button data-testid="save-question-btn" onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs px-5">
                  <Check className="w-3 h-3 mr-1.5" />{editing ? "Update Question" : "Save Question"}
                </Button>
                <Button data-testid="cancel-question-btn" variant="outline" onClick={cancel} className="rounded-sm text-xs border-[#D1D5DB] text-[#6B7280] hover:text-[#111827]">
                  <X className="w-3 h-3 mr-1" />Cancel
                </Button>
              </div>
            </div>
            );
          })()}

          {/* Question Grid */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="question-table">
            <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
              <p className="text-xs font-medium text-[#111827]">
                {ASSESSMENT_MAP[activeAssessment]?.label} · {activeModule ? MODULE_NUM[activeModule] : ""} · {activeSectionLabel}
              </p>
              <p className="text-[10px] text-[#9CA3AF]">{gridItems.length} question{gridItems.length !== 1 ? "s" : ""}{filtersActive ? " (filtered)" : ""}</p>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-white">
                  <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Order</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium min-w-[220px]">Question</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-28">Assessment</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-20">Module</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-32">Section</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-24">Answer Type</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-28">Eval Method</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-20">Impact</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-24">Trigger</th>
                  <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Status</th>
                  <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-16">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="question-tbody">
                {gridItems.map((q, idx) => {
                  const isDragging = dragId === q.id;
                  const isDropTarget = dragOverId === q.id && dragId && dragId !== q.id;
                  const secLabel = q.subsection_heading || sectionNavLabel(q.section);
                  return (
                    <tr
                      key={q.id}
                      data-testid={`question-row-${q.id}`}
                      draggable={canReorder && !reordering}
                      onDragStart={(e) => onDragStart(e, q.id)}
                      onDragOver={(e) => onDragOver(e, q.id)}
                      onDragLeave={() => onDragLeave(q.id)}
                      onDrop={(e) => onDrop(e, q.id)}
                      onDragEnd={onDragEnd}
                      className={`border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors align-top ${isDragging ? "opacity-40" : ""} ${isDropTarget ? "bg-[#E0F2FE] border-t-2 border-t-[#025979]" : ""}`}
                    >
                      <td className="px-2 py-2.5">
                        <div className="flex items-center justify-center gap-0.5">
                          {canReorder && (
                            <span data-testid={`drag-handle-${q.id}`} className="cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#025979]" title="Drag to reorder">
                              <GripVertical className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <span className="text-[#9CA3AF] text-[10px] w-4 text-center">{q.display_order || idx + 1}</span>
                          {canReorder && (
                            <div className="flex flex-col">
                              <button data-testid={`move-up-${q.id}`} onClick={() => moveUp(idx)} disabled={idx === 0 || reordering} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed" title="Move up"><ArrowUp className="w-3 h-3" /></button>
                              <button data-testid={`move-down-${q.id}`} onClick={() => moveDown(idx)} disabled={idx === gridItems.length - 1 || reordering} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed" title="Move down"><ArrowDown className="w-3 h-3" /></button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-[#111827]">
                        <div className="leading-snug">{q.question_text}</div>
                        {q.field_key && <div className="text-[9px] text-[#9CA3AF] font-mono mt-0.5">{q.field_key}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col gap-1">
                          {(q.evaluation_types || []).map(t => <AssessmentBadge key={t} type={t} />)}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{MODULE_NUM[q.module] || q.module}</td>
                      <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{secLabel}</td>
                      <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{ANSWER_TYPE_LABELS[q.answer_type] || q.answer_type}</td>
                      <td className="px-3 py-2.5"><EvalMethodBadge method={q.evaluation_method} /></td>
                      <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{impactLabel(q.score_category)}</td>
                      <td className="px-3 py-2.5">
                        {q.feature_flag_mapping && q.feature_flag_mapping !== "none" ? (
                          <span className="inline-block px-1.5 py-0.5 bg-[#E0F2FE] text-[#025979] rounded-sm text-[9px] font-medium">{triggerLabel(q.feature_flag_mapping)}</span>
                        ) : (<span className="text-[#D1D5DB]" title="No trigger">—</span>)}
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <span data-testid={`status-${q.id}`} className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${q.active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
                          {q.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <button data-testid={`edit-${q.id}`} onClick={() => openEdit(q)} className="text-[#025979] hover:text-[#03738C] mr-2" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-${q.id}`} onClick={() => setDeleteTarget(q.id)} className="text-red-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
                {gridItems.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-[#9CA3AF]">
                      {filtersActive ? "No questions match the current filters." : "No questions in this section yet."}
                      {!filtersActive && <button onClick={openAdd} className="text-[#025979] hover:text-[#03738C] ml-1 font-medium">Add one</button>}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Question</DialogTitle>
            <DialogDescription>Are you sure you want to delete this question? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button data-testid="confirm-delete-btn" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-xs">Delete Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
